import axios from 'axios';
import jwtDecode from 'jwt-decode';

const baseURL = process.env.REACT_APP_API_URL;

const $host = axios.create({
    withCredentials: true,
    baseURL
});

const $authHost = axios.create({
    withCredentials: true,
    baseURL
});

/*
 * Отдельный клиент для refresh.
 *
 * На нём нет auth-интерцептора, поэтому refresh
 * не сможет зациклиться сам на себя.
 */
const $refreshHost = axios.create({
    withCredentials: true,
    baseURL
});

let refreshPromise = null;
let refreshIdentity = null;

const getAccessToken = () => {
    const token = localStorage.getItem('token');

    if (
        !token ||
        token === 'null' ||
        token === 'undefined'
    ) {
        return null;
    }

    return token;
};

// Used only to detect account changes, never to authorize a request.
export const getAuthIdentity = () => {
    const token = getAccessToken();
    if (!token) return null;
    try { return `user:${jwtDecode(token).id}`; } catch { return `token:${token}`; }
};

const authContextChanged = () => Object.assign(
    new Error("Аккаунт изменился. Повторите действие в текущем аккаунте."),
    { code: "AUTH_CONTEXT_CHANGED" }
);

const setAuthorizationHeader = (
    config,
    token
) => {
    if (!config.headers) {
        config.headers = {};
    }

    /*
     * Axios 1.x может использовать AxiosHeaders.
     */
    if (
        typeof config.headers.set === 'function'
    ) {
        config.headers.set(
            'Authorization',
            `Bearer ${token}`
        );
    } else {
        config.headers.Authorization =
            `Bearer ${token}`;
    }
};

const removeAuthorizationHeader = (config) => {
    if (!config.headers) {
        return;
    }

    if (
        typeof config.headers.delete === 'function'
    ) {
        config.headers.delete('Authorization');
    } else {
        delete config.headers.Authorization;
        delete config.headers.authorization;
    }
};

const attachAccessToken = (config) => {
    const identity = getAuthIdentity();
    if (Object.prototype.hasOwnProperty.call(config, '_arentoAuthIdentity') &&
        config._arentoAuthIdentity !== identity) throw authContextChanged();
    config._arentoAuthIdentity = identity;
    const token = getAccessToken();

    if (token) {
        setAuthorizationHeader(config, token);
    } else {
        removeAuthorizationHeader(config);
    }

    return config;
};

$host.interceptors.request.use(
    attachAccessToken
);

$authHost.interceptors.request.use(
    attachAccessToken
);

const notifyAuthLogout = () => {
    localStorage.removeItem('token');

    window.dispatchEvent(
        new CustomEvent('auth:logout')
    );
};

/*
 * Все запросы в одной вкладке используют
 * один общий refreshPromise.
 */
const refreshAccessToken = async () => {
    const identity = getAuthIdentity();
    if (refreshPromise) {
        if (refreshIdentity !== identity) throw authContextChanged();
        return refreshPromise;
    }

    refreshIdentity = identity;
    const promise = $refreshHost.post('api/user/refresh').then(response => {
        const accessToken = response.data?.accessToken;
        if (!accessToken) throw new Error('Сервер не вернул accessToken');
        if (getAuthIdentity() !== identity) throw authContextChanged();
        // Do not replay an operation for A with a session restored for B.
        if (identity !== null && `user:${jwtDecode(accessToken).id}` !== identity) {
            throw authContextChanged();
        }
        localStorage.setItem('token', accessToken);
        return accessToken;
    }).finally(() => {
        if (refreshPromise === promise) {
            refreshPromise = null;
            refreshIdentity = null;
        }
    });
    refreshPromise = promise;
    return promise;
};

// Socket.IO calls this again on every handshake, including reconnects.
export const getFreshAccessToken = async () => {
    const token = getAccessToken();
    if (!token) throw authContextChanged();
    try {
        const { exp } = jwtDecode(token);
        if (Number.isFinite(exp) && exp * 1000 > Date.now() + 30000) return token;
    } catch { /* An unreadable/expired access token needs refresh. */ }
    return refreshAccessToken();
};

$authHost.interceptors.response.use(
    response => {
        if (response.config?._arentoAuthIdentity !== getAuthIdentity()) {
            throw authContextChanged();
        }
        return response;
    },

    async (error) => {
        const originalRequest = error.config;
        const status = error.response?.status;

        if (
            status !== 401 ||
            !originalRequest ||
            originalRequest._isRetry
        ) {
            return Promise.reject(error);
        }

        if (originalRequest._arentoAuthIdentity !== getAuthIdentity()) {
            return Promise.reject(authContextChanged());
        }
        const tokenBeforeRefresh = getAccessToken();
        originalRequest._isRetry = true;

        try {
            const newAccessToken =
                await refreshAccessToken();

            const restoredIdentity = getAuthIdentity();
            if (originalRequest._arentoAuthIdentity !== null &&
                originalRequest._arentoAuthIdentity !== restoredIdentity) throw authContextChanged();
            // A cold start may have a valid refresh cookie but no access token.
            originalRequest._arentoAuthIdentity = restoredIdentity;
            setAuthorizationHeader(originalRequest, newAccessToken);

            return $authHost(originalRequest);
        } catch (refreshError) {
            const refreshStatus =
                refreshError.response?.status;

            /*
             * Удаляем авторизацию только тогда,
             * когда сервер явно отверг refresh-токен.
             *
             * При обрыве интернета или ошибке 500
             * пользователя не разлогиниваем.
             */
            if (
                refreshStatus === 401 ||
                refreshStatus === 403
            ) {
                if (getAccessToken() === tokenBeforeRefresh &&
                    getAuthIdentity() === originalRequest._arentoAuthIdentity) notifyAuthLogout();
            }

            return Promise.reject(refreshError);
        }
    }
);

export {
    $host,
    $authHost,
    refreshAccessToken
};